import React, { useState, useEffect, useRef } from 'react';
import {
  Layout,
  Menu,
  Button,
  Avatar,
  Dropdown,
  Space,
  Input,
  Form,
  message,
  Drawer,
  Typography,
  Badge,
  Tag,
  Tooltip,
} from 'antd';
import {
  UserOutlined,
  SettingOutlined,
  DashboardOutlined,
  BookOutlined,
  LogoutOutlined,
  BarChartOutlined,
  TeamOutlined,
  ProjectOutlined,
  ReadOutlined,
  FileTextOutlined,
  TrophyOutlined,
  CalendarOutlined,
  CodeOutlined,
  BankOutlined,
  EnvironmentOutlined,
  TagsOutlined,
  SolutionOutlined,
  UsergroupAddOutlined,
  HomeOutlined,
  LockOutlined,
  KeyOutlined,
  ScheduleOutlined,
  IdcardOutlined,
  MenuOutlined,
  SearchOutlined,
  BellOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  ReloadOutlined,
  DownOutlined,
  AppstoreOutlined,
  ToolOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';

// ── Import your page components ──────────────────────────────────────────────
import {
  ArticleAdminPanel,
  AudienceForm,
  BlogForm,
  CorporateTraning,
  CoursesForm,
  CreateEmployerComponent,
  Developers,
  EventsDashboard,
  Home,
  JobWithLocation,
  Keyword,
  MarketAnalaysis,
  Projects,
  StudentTestimonial,
  Sucess,
  TrainerForm,
  UsersForm,
} from '../../features/admin/screens';
import { API_ENDPOINTS, apiFetch, getApiSourceMeta } from '../../services/api';
import { endSession, getSessionExpiryMs } from '../../services/auth';
import { ADMIN_KEY_BY_ROUTE, ADMIN_ROUTE_BY_KEY, DEFAULT_ADMIN_ROUTE } from '../../features/admin/navigation/routes';

const { Sider, Content, Header } = Layout;
const { Text, Title } = Typography;

// ── Types ─────────────────────────────────────────────────────────────────────
interface UserDetails {
  accessToken: string;
  emailId: string;
  mobile: string;
  name: string;
  role: string;
  userId: string;
  allowedMenuKeys?: string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const getUserDetails = (): UserDetails | null => {
  try {
    const raw = localStorage.getItem('userDetails');
    return raw ? (JSON.parse(raw) as UserDetails) : null;
  } catch {
    return null;
  }
};

const handleLogout = (): void => {
  localStorage.removeItem('userDetails');
  message.success('Logged out successfully');
  window.location.href = '/';
};

const toTitleCase = (str: string) =>
  str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

interface HeaderNotification {
  id: string;
  page: 'trainer' | 'blog' | 'artical';
  kind: 'Trainer' | 'Blog' | 'Article';
  title: string;
  description: string;
  createdAt?: string;
}

const relativeTime = (value?: string) => {
  if (!value) return 'Date unavailable';
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return 'Date unavailable';
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ── Menu definition ───────────────────────────────────────────────────────────
const ALL_MENU_ITEMS = [
  {
    key: 'dashboard',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
  },
  {
    key: 'content',
    icon: <AppstoreOutlined />,
    label: 'Content Management',
    children: [
      { key: 'blog', icon: <ReadOutlined />, label: 'Blog' },
      { key: 'artical', icon: <FileTextOutlined />, label: 'Articles' },
      { key: 'success', icon: <TrophyOutlined />, label: 'Success Stories' },
      { key: 'events', icon: <CalendarOutlined />, label: 'Events' },
      { key: 'courses', icon: <BookOutlined />, label: 'Courses' },
      { key: 'home', icon: <HomeOutlined />, label: 'Home Content' },
      { key: 'corporate-training', icon: <ScheduleOutlined />, label: 'Corporate Training' },
      { key: 'student-testimonial', icon: <FileTextOutlined />, label: 'Student Testimonials' },
    ],
  },
  {
    key: 'trainer-management',
    icon: <IdcardOutlined />,
    label: 'Trainer Management',
    children: [
      { key: 'trainer', icon: <IdcardOutlined />, label: 'Trainers' },
      // { key: 'trainer-requests', icon: <SolutionOutlined />, label: 'Trainer Applications / Requests' },
    ],
  },
  {
    key: 'users-management',
    icon: <UsergroupAddOutlined />,
    label: 'Users Management',
    children: [
      { key: 'users', icon: <UserOutlined />, label: 'Users' },
      { key: 'audience', icon: <TeamOutlined />, label: 'Audience' },
    ],
  },
  {
    key: 'jobs-employment',
    icon: <SolutionOutlined />,
    label: 'Jobs & Employment',
    children: [
      { key: 'job', icon: <EnvironmentOutlined />, label: 'Jobs & Locations' },
      { key: 'builder', icon: <BankOutlined />, label: 'Builder' },
      { key: 'MarketAnalaysis', icon: <BarChartOutlined />, label: 'Market Analysis' },
    ],
  },
  {
    key: 'dev-tools',
    icon: <ToolOutlined />,
    label: 'Developer Tools',
    children: [
      { key: 'developers', icon: <CodeOutlined />, label: 'Developers' },
      { key: 'projects', icon: <ProjectOutlined />, label: 'Projects' },
    ],
  },
  {
    key: 'seo-metadata',
    icon: <TagsOutlined />,
    label: 'SEO & Metadata',
    children: [
      { key: 'keyword', icon: <TagsOutlined />, label: 'Metadata' },
    ],
  },
  { key: 'settings', icon: <SettingOutlined />, label: 'Settings' },
];

const ALWAYS_ALLOWED = new Set(['audience', 'corporate-training', 'student-testimonial', 'trainer', 'trainer-requests']);

// ── Filter menu by allowedMenuKeys ────────────────────────────────────────────
function filterMenuItems(
  items: typeof ALL_MENU_ITEMS,
  allowed: string[],
): any[] {
  const allowedSet = new Set(allowed);

  return items.reduce<any[]>((acc, item) => {
    if (item.children) {
      const kids = item.children.filter(
        (c) => allowedSet.has(c.key) || ALWAYS_ALLOWED.has(c.key),
      );
      if (kids.length) acc.push({ ...item, children: kids });
    } else if (allowedSet.has(item.key) || ALWAYS_ALLOWED.has(item.key)) {
      acc.push(item);
    }
    return acc;
  }, []);
}

// ── Dashboard home: quick-access overview of every section ────────────────────
const DASHBOARD_HOME_STYLES = `
  .ireed-dashboard-home { width: 100%; color: #273247; text-align: left; }
  .ireed-dashboard-home *, .ireed-dashboard-home *::before, .ireed-dashboard-home *::after { box-sizing: border-box; }
  .ireed-dash-banner {
    background: linear-gradient(135deg, #1677ff 0%, #0b4fc9 100%);
    border-radius: 14px;
    padding: 24px 28px;
    color: #fff;
    margin-bottom: 20px;
    box-shadow: 0 8px 24px rgba(22,119,255,0.2);
  }

  /* ── KPI row ── */
  .ireed-kpi-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 16px;
  }
  @media (max-width: 900px) {
    .ireed-kpi-row { grid-template-columns: repeat(2, 1fr); }
  }
  .ireed-kpi-tile {
    background: #fff;
    border: 1px solid #eef1f6;
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 76px;
    text-align: left;
  }
  .ireed-kpi-copy { min-width: 0; display: flex; flex-direction: column; align-items: flex-start; }
  .ireed-kpi-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: #fff;
    flex-shrink: 0;
  }
  .ireed-kpi-label {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.4;
    white-space: nowrap;
  }
  .ireed-kpi-value {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
    font-variant-numeric: tabular-nums;
  }

  /* ── Chart cards ── */
  .ireed-charts-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }
  @media (max-width: 900px) {
    .ireed-charts-row { grid-template-columns: 1fr; }
  }
  .ireed-chart-card {
    background: #fff;
    border: 1px solid #eef1f6;
    border-radius: 14px;
    padding: 18px 20px;
    text-align: left;
  }
  .ireed-chart-title {
    font-size: 13.5px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 4px;
    line-height: 1.4;
  }
  .ireed-chart-subtitle {
    font-size: 12px;
    color: #9ca3af;
    margin-bottom: 16px;
    line-height: 1.5;
  }

  /* Legend */
  .ireed-legend {
    display: flex;
    gap: 16px;
    margin-bottom: 14px;
    flex-wrap: wrap;
  }
  .ireed-legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: #374151;
    line-height: 1.4;
    text-align: left;
  }
  .ireed-legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex-shrink: 0;
  }
  /* Gauge (circular meter) */
  .ireed-gauge-row {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  /* Experience bar list */
  .ireed-exp-bar-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .ireed-exp-bar-label {
    width: 62px;
    flex-shrink: 0;
    font-size: 12px;
    color: #6b7280;
    text-align: left;
  }
  .ireed-exp-bar-track {
    flex: 1;
    height: 12px;
    border-radius: 6px;
    background: #f0f0f0;
    overflow: hidden;
  }
  .ireed-exp-bar-fill {
    height: 100%;
    border-radius: 6px;
    background: #1677ff;
    transition: width 0.4s ease;
  }
  .ireed-exp-bar-count {
    width: 22px;
    flex-shrink: 0;
    text-align: right;
    font-size: 12.5px;
    font-weight: 600;
    color: #374151;
  }

  /* Recent trainers list */
  .ireed-recent-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 6px;
    border-radius: 8px;
    cursor: pointer;
    text-align: left;
  }
  .ireed-recent-item:hover {
    background: #f3f6fb;
  }

  .ireed-panel-empty {
    color: #9ca3af;
    font-size: 12.5px;
    padding: 20px 0;
    text-align: center;
  }

  /* Bottom summary strip */
  .ireed-summary-strip {
    display: flex;
    flex-wrap: wrap;
    background: #fff;
    border: 1px solid #eef1f6;
    border-radius: 14px;
    padding: 16px 8px;
    margin-bottom: 16px;
  }
  .ireed-summary-chip {
    flex: 1 1 0;
    min-width: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 6px 10px;
    font-size: 15px;
    text-align: left;
  }
  .ireed-summary-value {
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  }
  .ireed-summary-label {
    font-size: 11.5px;
    color: #9ca3af;
    line-height: 1.3;
  }
`;

const DashboardHome: React.FC<{
  items: any[];
  pendingTrainerCount: number;
  trainerStats: { total: number; verified: number; avgRating: number };
  audienceStats: { total: number; fresher: number; experienced: number };
  experienceBuckets: { label: string; count: number }[];
  recentTrainers: { id: string; name: string; subtitle: string; verified: boolean; date: string | null; photo?: string }[];
  userName?: string;
  userRole?: string;
  onNavigate: (key: string) => void;
}> = ({
  items,
  pendingTrainerCount,
  trainerStats,
  audienceStats,
  experienceBuckets,
  recentTrainers,
  userName,
  userRole,
  onNavigate,
}) => {
    const today = new Date().toLocaleDateString('en-IN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    const verifiedPct = trainerStats.total ? Math.round((trainerStats.verified / trainerStats.total) * 100) : 0;
    const fresherPct = audienceStats.total ? (audienceStats.fresher / audienceStats.total) * 100 : 0;
    const experiencedPct = audienceStats.total ? (audienceStats.experienced / audienceStats.total) * 100 : 0;

    const gaugeRadius = 52;
    const gaugeCircumference = 2 * Math.PI * gaugeRadius;
    const gaugeDash = (verifiedPct / 100) * gaugeCircumference;
    const maxBucketCount = Math.max(1, ...experienceBuckets.map((b) => b.count));

    return (
      <div className="ireed-dashboard-home">
        <style>{DASHBOARD_HOME_STYLES}</style>
        {/* <div className="ireed-dash-banner">
        <Title level={3} style={{ color: '#fff', margin: 0 }}>
          Welcome back, {userName || 'Admin'}
        </Title>
        <Text style={{ color: 'rgba(255,255,255,0.85)' }}>
          {userRole ? `${toTitleCase(userRole)} · ` : ''}
          {today}
        </Text>
      </div> */}

        <div className="ireed-kpi-row">
          <div className="ireed-kpi-tile">
            <div className="ireed-kpi-icon" style={{ background: 'linear-gradient(135deg, #1677ff, #0b4fc9)' }}>
              <IdcardOutlined />
            </div>
            <div className="ireed-kpi-copy">
              <div className="ireed-kpi-label">Total Trainers</div>
              <div className="ireed-kpi-value">{trainerStats.total}</div>
            </div>
          </div>
          <div className="ireed-kpi-tile">
            <div className="ireed-kpi-icon" style={{ background: 'linear-gradient(135deg, #73d13d, #389e0d)' }}>
              <UserOutlined />
            </div>
            <div className="ireed-kpi-copy">
              <div className="ireed-kpi-label">Verified Trainers</div>
              <div className="ireed-kpi-value">{trainerStats.verified}</div>
            </div>
          </div>
          <div className="ireed-kpi-tile">
            <div className="ireed-kpi-icon" style={{ background: 'linear-gradient(135deg, #ffd666, #fa8c16)' }}>
              <TrophyOutlined />
            </div>
            <div className="ireed-kpi-copy">
              <div className="ireed-kpi-label">Avg Trainer Rating</div>
              <div className="ireed-kpi-value">{trainerStats.avgRating.toFixed(1)}</div>
            </div>
          </div>
          <div className="ireed-kpi-tile">
            <div className="ireed-kpi-icon" style={{ background: 'linear-gradient(135deg, #b37feb, #722ed1)' }}>
              <TeamOutlined />
            </div>
            <div className="ireed-kpi-copy">
              <div className="ireed-kpi-label">Total Students</div>
              <div className="ireed-kpi-value">{audienceStats.total}</div>
            </div>
          </div>
        </div>

        <div className="ireed-charts-row">
          <div className="ireed-chart-card">
            <div className="ireed-chart-title">Trainer profile verification</div>
            <div className="ireed-chart-subtitle">
              {trainerStats.verified} of {trainerStats.total} trainer profiles verified
            </div>
            <div className="ireed-gauge-row">
              <svg width={128} height={128} viewBox="0 0 128 128">
                <circle cx={64} cy={64} r={gaugeRadius} fill="none" stroke="#eef1f6" strokeWidth={13} />
                <circle
                  cx={64}
                  cy={64}
                  r={gaugeRadius}
                  fill="none"
                  stroke="#1677ff"
                  strokeWidth={13}
                  strokeLinecap="round"
                  strokeDasharray={`${gaugeDash} ${gaugeCircumference - gaugeDash}`}
                  transform="rotate(-90 64 64)"
                />
                <text x={64} y={60} textAnchor="middle" fontSize={22} fontWeight={700} fill="#111827">
                  {verifiedPct}%
                </text>
                <text x={64} y={78} textAnchor="middle" fontSize={11} fill="#9ca3af">
                  Verified
                </text>
              </svg>
              <div className="ireed-legend" style={{ flexDirection: 'column', gap: 10, marginBottom: 0 }}>
                <span className="ireed-legend-item">
                  <span className="ireed-legend-dot" style={{ background: '#1677ff' }} />
                  Verified ({trainerStats.verified})
                </span>
                <span className="ireed-legend-item">
                  <span className="ireed-legend-dot" style={{ background: '#eef1f6' }} />
                  Pending Review ({pendingTrainerCount})
                </span>
              </div>
            </div>
          </div>

          <div className="ireed-chart-card">
            <div className="ireed-chart-title">Students by work status</div>
            <div className="ireed-chart-subtitle">{audienceStats.total} student profiles</div>
            <div className="ireed-gauge-row">
              <svg width={128} height={128} viewBox="0 0 128 128">
                <circle cx={64} cy={64} r={gaugeRadius} fill="none" stroke="#f0f0f0" strokeWidth={16} />
                {audienceStats.fresher > 0 && (
                  <circle
                    cx={64}
                    cy={64}
                    r={gaugeRadius}
                    fill="none"
                    stroke="#1677ff"
                    strokeWidth={16}
                    strokeDasharray={`${(fresherPct / 100) * gaugeCircumference} ${gaugeCircumference}`}
                    transform="rotate(-90 64 64)"
                  />
                )}
                {audienceStats.experienced > 0 && (
                  <circle
                    cx={64}
                    cy={64}
                    r={gaugeRadius}
                    fill="none"
                    stroke="#fa8c16"
                    strokeWidth={16}
                    strokeDasharray={`${(experiencedPct / 100) * gaugeCircumference} ${gaugeCircumference}`}
                    strokeDashoffset={-((fresherPct / 100) * gaugeCircumference)}
                    transform="rotate(-90 64 64)"
                  />
                )}
                <text x={64} y={60} textAnchor="middle" fontSize={22} fontWeight={700} fill="#111827">
                  {audienceStats.total}
                </text>
                <text x={64} y={78} textAnchor="middle" fontSize={11} fill="#9ca3af">
                  Total
                </text>
              </svg>
              <div className="ireed-legend" style={{ flexDirection: 'column', gap: 10, marginBottom: 0 }}>
                <span className="ireed-legend-item">
                  <span className="ireed-legend-dot" style={{ background: '#1677ff' }} />
                  Fresher ({audienceStats.fresher}{audienceStats.total ? `, ${Math.round(fresherPct)}%` : ''})
                </span>
                <span className="ireed-legend-item">
                  <span className="ireed-legend-dot" style={{ background: '#fa8c16' }} />
                  Experienced ({audienceStats.experienced}{audienceStats.total ? `, ${Math.round(experiencedPct)}%` : ''})
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="ireed-charts-row">
          <div className="ireed-chart-card">
            <div className="ireed-chart-title">Trainers by experience</div>
            <div className="ireed-chart-subtitle">Bucketed from each trainer's years of experience</div>
            {experienceBuckets.some((b) => b.count > 0) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {experienceBuckets.map((b) => (
                  <div key={b.label} className="ireed-exp-bar-row">
                    <span className="ireed-exp-bar-label">{b.label}</span>
                    <div className="ireed-exp-bar-track">
                      <div
                        className="ireed-exp-bar-fill"
                        style={{ width: `${Math.max(4, (b.count / maxBucketCount) * 100)}%` }}
                      />
                    </div>
                    <span className="ireed-exp-bar-count">{b.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ireed-panel-empty">No trainer experience data yet.</div>
            )}
          </div>

          <div className="ireed-chart-card">
            <div className="ireed-chart-title">Recent trainer registrations</div>
            <div className="ireed-chart-subtitle">Newest trainer profiles</div>
            {recentTrainers.length ? (
              <div>
                {recentTrainers.map((t) => (
                  <div key={t.id} className="ireed-recent-item" onClick={() => onNavigate('trainer')}>
                    <Avatar size={32} icon={<UserOutlined />} src={t.photo} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{t.name}</div>
                      {t.subtitle && (
                        <div style={{ fontSize: 11.5, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.subtitle}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Tag color={t.verified ? 'green' : 'orange'} style={{ margin: 0 }}>
                        {t.verified ? 'Verified' : 'Pending'}
                      </Tag>
                      {t.date && <div style={{ fontSize: 10.5, color: '#c0c4cc', marginTop: 3 }}>{t.date}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ireed-panel-empty">No trainers registered yet.</div>
            )}
          </div>
        </div>

        <div className="ireed-summary-strip">
          <div className="ireed-summary-chip">
            <IdcardOutlined style={{ color: '#1677ff' }} />
            <span className="ireed-summary-value">{trainerStats.total}</span>
            <span className="ireed-summary-label">Total Trainers</span>
          </div>
          <div className="ireed-summary-chip">
            <UserOutlined style={{ color: '#389e0d' }} />
            <span className="ireed-summary-value">{trainerStats.verified}</span>
            <span className="ireed-summary-label">Verified Trainers</span>
          </div>
          <div className="ireed-summary-chip">
            <BellOutlined style={{ color: '#fa8c16' }} />
            <span className="ireed-summary-value">{pendingTrainerCount}</span>
            <span className="ireed-summary-label">Pending Review</span>
          </div>
          <div className="ireed-summary-chip">
            <TeamOutlined style={{ color: '#722ed1' }} />
            <span className="ireed-summary-value">{audienceStats.total}</span>
            <span className="ireed-summary-label">Total Students</span>
          </div>
          <div className="ireed-summary-chip">
            <UserOutlined style={{ color: '#1677ff' }} />
            <span className="ireed-summary-value">{audienceStats.fresher}</span>
            <span className="ireed-summary-label">Freshers</span>
          </div>
          <div className="ireed-summary-chip">
            <UserOutlined style={{ color: '#fa8c16' }} />
            <span className="ireed-summary-value">{audienceStats.experienced}</span>
            <span className="ireed-summary-label">Experienced</span>
          </div>
        </div>

      </div>
    );
  };

// ── Placeholder for modules without a backend endpoint yet ────────────────────
const ComingSoonPage: React.FC<{ title: string }> = ({ title }) => (
  <div
    style={{
      height: '100%',
      minHeight: 300,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#8c8c8c',
      background: '#fff',
      borderRadius: 12,
      padding: 40,
      textAlign: 'center',
    }}
  >
    <SolutionOutlined style={{ fontSize: 32, marginBottom: 12, color: '#bfbfbf' }} />
    <Text strong style={{ fontSize: 16, color: '#595959' }}>{title}</Text>
    <Text type="secondary" style={{ maxWidth: 360, marginTop: 8, fontSize: 13 }}>
      This section isn't connected to an API yet. Share the endpoint for it and it can be wired up.
    </Text>
  </div>
);

// ── Page renderer ─────────────────────────────────────────────────────────────
const PAGE_MAP: Record<string, React.ReactNode> = {
  users: <UsersForm />,
  courses: <CoursesForm />,
  MarketAnalaysis: <MarketAnalaysis />,
  developers: <Developers />,
  projects: <Projects />,
  success: <Sucess />,
  keyword: <Keyword />,
  home: <Home />,
  job: <JobWithLocation />,
  builder: <CreateEmployerComponent />,
  artical: <ArticleAdminPanel />,
  blog: <BlogForm />,
  events: <EventsDashboard />,
  audience: <AudienceForm />,
  trainer: <TrainerForm />,
  // 'trainer-requests': <ComingSoonPage title="Trainer Applications / Requests" />,
  'corporate-training': <CorporateTraning />,
  'student-testimonial': <StudentTestimonial />,
};

// ── Sidebar styles (scoped, no global bleed) ──────────────────────────────────
const SIDER_STYLES = `
  .ireed-sider .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  /* ── Menu base ── */
  .ireed-menu.ant-menu-dark {
    background: transparent !important;
    border-inline-end: none !important;
  }

  /* ── Top-level items & submenu titles ── */
  .ireed-menu .ant-menu-item,
  .ireed-menu .ant-menu-submenu-title {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    height: 40px !important;
    line-height: 40px !important;
    margin: 1px 10px !important;
    width: calc(100% - 20px) !important;
    border-radius: 6px !important;
    padding-left: 14px !important;
    color: rgba(255,255,255,0.72) !important;
    font-size: 13.5px !important;
    font-weight: 500 !important;
    transition: background 0.15s, color 0.15s !important;
  }

  /* ── Icons: consistent size & alignment ── */
  .ireed-menu .ant-menu-item .anticon,
  .ireed-menu .ant-menu-submenu-title .anticon {
    flex: none !important;
    font-size: 15px !important;
    min-width: 15px !important;
    vertical-align: -0.15em !important;
    color: rgba(255,255,255,0.55) !important;
    transition: color 0.15s !important;
  }

  /* ── Label: always left-aligned, fixed gap after the icon ── */
  .ireed-menu .ant-menu-title-content {
    flex: none !important;
    margin-inline-start: 10px !important;
    text-align: left !important;
  }

  /* ── Hover ── */
  .ireed-menu .ant-menu-item:hover,
  .ireed-menu .ant-menu-submenu-title:hover {
    background: rgba(255,255,255,0.07) !important;
    color: #fff !important;
  }
  .ireed-menu .ant-menu-item:hover .anticon,
  .ireed-menu .ant-menu-submenu-title:hover .anticon {
    color: #fff !important;
  }

  /* ── Selected ── */
  .ireed-menu .ant-menu-item-selected {
    background: #1677ff !important;
    color: #fff !important;
  }
  .ireed-menu .ant-menu-item-selected .anticon {
    color: #fff !important;
  }
  .ireed-menu .ant-menu-item-selected::after {
    display: none !important;
  }

  /* ── Sub-menu panel ── */
  .ireed-menu .ant-menu-sub.ant-menu-inline {
    background: transparent !important;
    padding: 3px 0 5px !important;
  }
  .ireed-menu .ant-menu-sub .ant-menu-item {
    height: 36px !important;
    line-height: 36px !important;
    margin: 1px 10px !important;
    width: calc(100% - 20px) !important;
    padding-left: 18px !important;
    font-size: 13px !important;
    font-weight: 400 !important;
  }

  /* ── Submenu arrow ── */
  .ireed-menu .ant-menu-submenu-arrow {
    color: rgba(255,255,255,0.35) !important;
    right: 14px !important;
  }
  .ireed-menu .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-submenu-arrow {
    color: rgba(255,255,255,0.65) !important;
  }

  /* ── Sign out footer ── */
  .ireed-signout {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 48px;
    color: #ff6b6b;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    border-top: 1px solid rgba(255,255,255,0.08);
    transition: background 0.15s;
  }
  .ireed-signout:hover {
    background: rgba(255,107,107,0.08);
  }

  /* ── Header: hamburger / icon buttons ── */
  .ireed-hamburger {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #1677ff;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 15px;
    transition: background 0.15s;
    border: 0;
  }
  .ireed-hamburger:hover {
    background: #0b5cd6;
  }
  .ireed-icon-btn {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #f3f4f6;
    color: #4b5563;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 15px;
    transition: background 0.15s;
    border: 0;
  }
  .ireed-icon-btn:hover {
    background: #e5e7eb;
  }
  .ireed-environment-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 24px;
    padding: 0 9px;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 750;
    letter-spacing: .65px;
    line-height: 1;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .ireed-environment-dot { width: 6px; height: 6px; border-radius: 50%; }
  .ireed-environment-badge.live { color: #24734d; background: #edf9f1; border-color: #ccebd7; }
  .ireed-environment-badge.live .ireed-environment-dot { background: #36a269; box-shadow: 0 0 0 3px rgba(54,162,105,.13); }
  .ireed-environment-badge.uat { color: #a85c00; background: #fff6e7; border-color: #f5dbac; }
  .ireed-environment-badge.uat .ireed-environment-dot { background: #e89b23; box-shadow: 0 0 0 3px rgba(232,155,35,.14); }
  .ireed-environment-badge.mixed { color: #6941b5; background: #f5f0ff; border-color: #ded0ff; }
  .ireed-environment-badge.mixed .ireed-environment-dot { background: #8d63d9; box-shadow: 0 0 0 3px rgba(141,99,217,.14); }
  .ireed-notification-panel {
    width: 360px; max-height: min(480px, calc(100vh - 86px)); overflow: hidden;
    border: 1px solid #e8edf4; border-radius: 14px; background: #fff;
    box-shadow: 0 16px 42px rgba(15,23,42,.16);
  }
  .ireed-notification-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 16px 12px; border-bottom: 1px solid #edf0f4;
  }
  .ireed-notification-title { color: #172033; font-size: 14px; font-weight: 700; }
  .ireed-notification-count { padding: 2px 8px; border-radius: 10px; color: #2563eb; background: #edf4ff; font-size: 11px; font-weight: 650; }
  .ireed-notification-list { max-height: 390px; overflow-y: auto; padding: 6px; }
  .ireed-notification-item { display: flex; gap: 11px; padding: 10px; border-radius: 9px; cursor: pointer; transition: background .15s; }
  .ireed-notification-item:hover { background: #f7f9fc; }
  .ireed-notification-icon { display: grid; place-items: center; width: 34px; height: 34px; flex: 0 0 34px; border-radius: 9px; font-size: 14px; }
  .ireed-notification-icon.blog { color: #7c3aed; background: #f3efff; }
  .ireed-notification-icon.article { color: #d97706; background: #fff7e6; }
  .ireed-notification-icon.trainer { color: #2563eb; background: #edf4ff; }
  .ireed-notification-copy { min-width: 0; flex: 1; }
  .ireed-notification-item-title { overflow: hidden; color: #273247; font-size: 12.5px; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; }
  .ireed-notification-description { margin-top: 1px; color: #788397; font-size: 11.5px; }
  .ireed-notification-time { margin-top: 3px; color: #a0a9b7; font-size: 10.5px; }
  .ireed-notification-empty { padding: 34px 16px; color: #8a95a6; font-size: 12px; text-align: center; }

  /* ── Header: search ── */
  .ireed-search-wrap {
    position: relative;
  }
  .ireed-search-input.ant-input-affix-wrapper {
    width: 280px;
    background: #f3f4f6;
    border: none;
    border-radius: 20px;
    padding: 6px 12px;
  }
  .ireed-search-input.ant-input-affix-wrapper:focus,
  .ireed-search-input.ant-input-affix-wrapper-focused {
    box-shadow: 0 0 0 2px rgba(22,119,255,0.15);
  }
  .ireed-search-input .ant-input {
    background: transparent;
    font-size: 13px;
  }
  .ireed-kbd {
    font-size: 11px;
    color: #9ca3af;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 5px;
    padding: 1px 6px;
    white-space: nowrap;
  }
  .ireed-search-results {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    width: 320px;
    max-height: 320px;
    overflow-y: auto;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 10px 28px rgba(16,24,40,0.16);
    padding: 6px;
    z-index: 1100;
  }
  .ireed-search-result-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;
  }
  .ireed-search-result-item:hover {
    background: #f3f6fb;
  }
  .ireed-search-result-icon {
    color: #1677ff;
    font-size: 15px;
    width: 18px;
    text-align: center;
  }

  /* ── Section divider between top-level menu groups ── */
  .ireed-menu-divider {
    height: 1px !important;
    margin: 6px 16px !important;
    background: rgba(255,255,255,0.08) !important;
  }

  /* ── Scrollbar hide ── */
  .ireed-menu-scroll::-webkit-scrollbar { width: 0; }
  .ireed-menu-scroll { scrollbar-width: none; }
`;

// ═════════════════════════════════════════════════════════════════════════════
// AdminDashboard
// ═════════════════════════════════════════════════════════════════════════════
const AdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const selectedKey = ADMIN_KEY_BY_ROUTE[location.pathname] || 'dashboard';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pendingTrainerCount, setPendingTrainerCount] = useState(0);
  const [notifications, setNotifications] = useState<HeaderNotification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  useEffect(() => {
    const expiry = getSessionExpiryMs();
    if (!expiry) {
      endSession();
      return;
    }
    const timeoutId = window.setTimeout(endSession, Math.max(0, expiry - Date.now()));
    return () => window.clearTimeout(timeoutId);
  }, []);
  const [trainerStats, setTrainerStats] = useState({ total: 0, verified: 0, avgRating: 0 });
  const [audienceStats, setAudienceStats] = useState({ total: 0, fresher: 0, experienced: 0 });
  const [experienceBuckets, setExperienceBuckets] = useState<{ label: string; count: number }[]>([]);
  const [recentTrainers, setRecentTrainers] = useState<
    { id: string; name: string; subtitle: string; verified: boolean; date: string | null; photo?: string }[]
  >([]);
  const searchInputRef = useRef<any>(null);

  const userDetails = getUserDetails();

  const allowedMenuKeys: string[] = (() => {
    try {
      const raw = localStorage.getItem('userDetails');
      return raw ? (JSON.parse(raw).allowedMenuKeys ?? []) : [];
    } catch {
      return [];
    }
  })();

  const filteredItems = filterMenuItems(ALL_MENU_ITEMS, allowedMenuKeys);

  // Flat, searchable list of every page the current user can navigate to
  const flatSearchItems: { key: string; label: string; icon: React.ReactNode; group?: string }[] = (() => {
    const flat: { key: string; label: string; icon: React.ReactNode; group?: string }[] = [];
    filteredItems.forEach((item) => {
      if (item.children) {
        item.children.forEach((child: any) => flat.push({ key: child.key, label: child.label, icon: child.icon, group: item.label }));
      } else {
        flat.push({ key: item.key, label: item.label, icon: item.icon });
      }
    });
    return flat;
  })();
  const accessibleRouteSignature = flatSearchItems.map((item) => item.key).sort().join('|');

  const searchResults = searchQuery.trim()
    ? flatSearchItems.filter((item) => item.label.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : [];

  const currentPageLabel = selectedKey === 'dashboard'
    ? 'Dashboard'
    : flatSearchItems.find((item) => item.key === selectedKey)?.label || 'Dashboard';
  const pageEndpointUrls = selectedKey === 'trainer'
    ? [API_ENDPOINTS.trainers]
    : selectedKey === 'dashboard'
      ? [API_ENDPOINTS.usersSearch, API_ENDPOINTS.trainers]
      : [API_ENDPOINTS.usersSearch];
  const dataSourceMeta = getApiSourceMeta(pageEndpointUrls);
  const environmentBadge = dataSourceMeta.label === 'UAT Service Data'
    ? { label: 'UAT', style: 'uat', tooltip: 'UAT testing service data' }
    : dataSourceMeta.label === 'Main + UAT Data'
      ? { label: 'Live + UAT', style: 'mixed', tooltip: 'Data from both Live and UAT services' }
      : { label: 'Live', style: 'live', tooltip: 'Live production service data' };

  const goToPage = (key: string) => {
    navigate(ADMIN_ROUTE_BY_KEY[key] || DEFAULT_ADMIN_ROUTE);
    setSearchQuery('');
    setSearchOpen(false);
  };

  useEffect(() => {
    const routeKey = ADMIN_KEY_BY_ROUTE[location.pathname];
    const isAllowed = routeKey === 'dashboard' || flatSearchItems.some((item) => item.key === routeKey);
    if (!routeKey || !isAllowed) navigate(DEFAULT_ADMIN_ROUTE, { replace: true });
  }, [location.pathname, navigate, accessibleRouteSignature]);

  // Ctrl+K focuses the quick-nav search box
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Header review queue: pending trainers plus unpublished editorial content.
  useEffect(() => {
    const loadNotifications = async () => {
      setNotificationsLoading(true);
      try {
        const [trainerRes, blogRes, articleRes] = await Promise.all([
          apiFetch(`${API_ENDPOINTS.trainers}?pageNumber=0&size=1000`),
          apiFetch(`${API_ENDPOINTS.blogs}?size=1000`),
          apiFetch(`${API_ENDPOINTS.articles}?pageNumber=0&size=1000`),
        ]);
        const [trainerJson, blogJson, articleJson] = await Promise.all([
          trainerRes.json(), blogRes.json(), articleRes.json(),
        ]);
        const trainers: any[] = Array.isArray(trainerJson?.data)
          ? trainerJson.data : trainerJson?.data?.content || (Array.isArray(trainerJson) ? trainerJson : []);
        const blogs: any[] = blogJson?.blogs || blogJson?.data?.blogs || blogJson?.data?.content || [];
        const articles: any[] = articleJson?.blogs || articleJson?.data?.blogs || articleJson?.data?.content || [];
        const contentDate = (item: any) => item?.userPostDataInfo?.[0]?.updatedAt ||
          item?.userPostDataInfo?.[0]?.createdAt || item?.updatedAt || item?.createdAt || item?.date || item?.publishedDate;

        const queue: HeaderNotification[] = [
          ...trainers.filter((item) => item.isProfileVerified !== true).map((item) => ({
            id: `trainer-${item._id}`, page: 'trainer' as const, kind: 'Trainer' as const,
            title: item.name || 'Unnamed trainer', description: 'Profile verification is pending', createdAt: item.createdAt,
          })),
          ...blogs.filter((item) => !(item.published ?? item.isPublished ?? false)).map((item) => ({
            id: `blog-${item._id}`, page: 'blog' as const, kind: 'Blog' as const,
            title: item.title || 'Untitled blog', description: 'Blog is unpublished', createdAt: contentDate(item),
          })),
          ...articles.filter((item) => !(item.published ?? item.isPublished ?? false)).map((item) => ({
            id: `article-${item._id}`, page: 'artical' as const, kind: 'Article' as const,
            title: item.title || 'Untitled article', description: 'Article is unpublished', createdAt: contentDate(item),
          })),
        ];
        queue.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
        setNotifications(queue);
      } catch {
        setNotifications([]);
      } finally {
        setNotificationsLoading(false);
      }
    };
    loadNotifications();
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const hardRefresh = () => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("refresh", Date.now().toString());
    window.location.replace(currentUrl.toString());
  };

  // Trainer stats — powers the header notification bell and the dashboard charts.
  useEffect(() => {
    const fetchTrainerStats = async () => {
      try {
        const res = await apiFetch(`${API_ENDPOINTS.trainers}?pageNumber=0&size=1000`);
        const json = await res.json();
        const trainers: any[] = Array.isArray(json?.data)
          ? json.data
          : json?.data?.content || (Array.isArray(json) ? json : []);
        const total = json?.totalItems || json?.totalElements || json?.total || trainers.length;
        const verifiedTrainers = trainers.filter((t) => t.isProfileVerified === true);
        const ratedTrainers = trainers.filter((t) => !!t.rating);
        setPendingTrainerCount(trainers.filter((t) => t.isProfileVerified !== true).length);
        setTrainerStats({
          total,
          verified: verifiedTrainers.length,
          avgRating: ratedTrainers.length
            ? ratedTrainers.reduce((sum, t) => sum + (t.rating || 0), 0) / ratedTrainers.length
            : 0,
        });

        // Real experience-level breakdown, bucketed from each trainer's own experienceYears.
        const buckets = [
          { label: '0–2 yrs', min: 0, max: 2 },
          { label: '3–5 yrs', min: 3, max: 5 },
          { label: '6–10 yrs', min: 6, max: 10 },
          { label: '10+ yrs', min: 11, max: Infinity },
        ];
        setExperienceBuckets(
          buckets.map((b) => ({
            label: b.label,
            count: trainers.filter((t) => {
              const y = t.experienceYears;
              return typeof y === 'number' && y >= b.min && y <= b.max;
            }).length,
          }))
        );

        // Real recent-trainer list, newest first when createdAt is available.
        const sorted = [...trainers].sort((a, b) => {
          const ad = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bd = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bd - ad;
        });
        setRecentTrainers(
          sorted.slice(0, 5).map((t) => ({
            id: t._id,
            name: t.name || 'Unnamed trainer',
            subtitle: t.subjectLine || t.trainerType || '',
            verified: t.isProfileVerified === true,
            date: t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null,
            photo: t.profilePhoto,
          }))
        );
      } catch {
        // Supplementary dashboard data; ignore failures.
      }
    };
    fetchTrainerStats();
  }, []);

  // Audience (student) split by work status — feeds the dashboard's stacked bar.
  useEffect(() => {
    const fetchAudienceStats = async () => {
      const storedDetails = localStorage.getItem('userDetails');
      const token = storedDetails ? JSON.parse(storedDetails)?.accessToken : null;
      if (!token) return;
      try {
        const fetchCount = async (workStatus?: string) => {
          const body: Record<string, unknown> = {
            pageNumber: '0',
            size: '1',
            role: ['student'],
          };
          if (workStatus) body.workStatus = workStatus;

          const res = await apiFetch(API_ENDPOINTS.usersSearch, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body),
          });
          const json = await res.json();
          const students = Array.isArray(json?.data) ? json.data : json?.data?.content || [];
          return json?.totalItems ?? json?.totalElements ?? json?.total ?? students.length;
        };
        const [total, fresher, experienced] = await Promise.all([
          fetchCount(),
          fetchCount('Fresher'),
          fetchCount('Experienced'),
        ]);
        setAudienceStats({ total, fresher, experienced });
      } catch {
        // Supplementary dashboard data; ignore failures.
      }
    };
    fetchAudienceStats();
  }, []);

  // Build AntD menu items (recursive)
  const buildItems = (items: any[]): MenuProps['items'] =>
    items.map((item) => ({
      key: item.key,
      icon: item.icon,
      label: item.label,
      ...(item.children ? { children: buildItems(item.children) } : {}),
    }));

  // Insert a divider between each top-level section so the sidebar reads as
  // distinct groups instead of one long unbroken list.
  const buildTopLevelItemsWithDividers = (items: any[]): MenuProps['items'] =>
    (buildItems(items) || []).flatMap((item, idx) =>
      idx === 0 ? [item] : [{ type: 'divider', key: `divider-${idx}`, className: 'ireed-menu-divider' }, item]
    );

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'changepass',
      icon: <KeyOutlined />,
      label: 'Change Password',
      onClick: () => setDrawerOpen(true),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: () => {
        if (window.confirm('Are you sure you want to logout?')) handleLogout();
      },
    },
  ];

  return (
    <Layout className="ireed-admin-layout" style={{ minHeight: '100vh', height: '100vh', overflow: 'hidden', textAlign: 'left' }}>
      <style>{SIDER_STYLES}</style>

      {/* ── Sidebar ── */}
      <Sider
        className="ireed-sider"
        collapsible
        trigger={null}
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={224}
        collapsedWidth={64}
        style={{
          height: '100vh',
          background: 'linear-gradient(180deg, #0b1727 0%, #102040 100%)',
          boxShadow: '2px 0 12px rgba(0,0,0,0.35)',
          overflow: 'hidden',
        }}
      >
        {/* Logo bar */}
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            flexShrink: 0,
          }}
        >
          <img
            src="https://ireedindia.com/assets/images/logo/logo-darkq.png"
            alt="iReed"
            style={{ height: collapsed ? 28 : 40, objectFit: 'contain' }}
          />
        </div>

        {/* Scrollable menu */}
        <div
          className="ireed-menu-scroll"
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingTop: 6,
          }}
        >
          <Menu
            className="ireed-menu"
            theme="dark"
            mode="inline"
            selectedKeys={[selectedKey]}
            openKeys={collapsed ? [] : openKeys}
            onOpenChange={(keys) => setOpenKeys(keys as string[])}
            onClick={({ key }) => goToPage(key)}
            inlineIndent={12}
            items={buildTopLevelItemsWithDividers(filteredItems)}
          />
        </div>

        {/* Sign out */}
        <div
          className="ireed-signout"
          onClick={() => {
            if (window.confirm('Are you sure you want to logout?')) handleLogout();
          }}
        >
          <LogoutOutlined />
          {!collapsed && <span>Sign Out</span>}
        </div>
      </Sider>

      {/* ── Main layout ── */}
      <Layout style={{ overflow: 'hidden', height: '100vh', minWidth: 0, background: '#f5f6fa' }}>
        {/* Header */}
        <Header
          className="ireed-global-header"
          style={{
            background: '#fff',
            padding: '0 20px',
            height: 64,
            lineHeight: '64px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            flexShrink: 0,
            gap: 16,
            position: 'relative',
            zIndex: 100,
          }}
        >
          <Space size={14} align="center">
            <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
              <button
                type="button"
                className="ireed-hamburger"
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                onClick={() => setCollapsed((prev) => !prev)}
              >
                <MenuOutlined />
              </button>
            </Tooltip>
            <div style={{ width: 1, height: 22, background: '#e5e7eb' }} />
            <Space size={8} align="center">
              <Text strong style={{ fontSize: 15, color: '#111827' }}>{currentPageLabel}</Text>
              <Tooltip title={environmentBadge.tooltip}>
                <span className={`ireed-environment-badge ${environmentBadge.style}`}>
                  <span className="ireed-environment-dot" />
                  {environmentBadge.label}
                </span>
              </Tooltip>
            </Space>
          </Space>

          <Space size={14} align="center">
            <div className="ireed-search-wrap">
              <Input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                placeholder="Search here..."
                prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
                suffix={<span className="ireed-kbd">Ctrl + K</span>}
                className="ireed-search-input"
              />
              {searchOpen && searchQuery.trim() && (
                <div className="ireed-search-results">
                  {searchResults.length ? (
                    searchResults.map((item) => (
                      <div
                        key={item.key}
                        className="ireed-search-result-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => goToPage(item.key)}
                      >
                        <span className="ireed-search-result-icon">{item.icon}</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{item.label}</div>
                          {item.group && <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.group}</div>}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '14px 12px', fontSize: 12.5, color: '#9ca3af', textAlign: 'center' }}>
                      No pages match "{searchQuery}"
                    </div>
                  )}
                </div>
              )}
            </div>

            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              open={notificationsOpen}
              onOpenChange={setNotificationsOpen}
              dropdownRender={() => (
                <div className="ireed-notification-panel">
                  <div className="ireed-notification-head">
                    <span className="ireed-notification-title">Notifications</span>
                    <span className="ireed-notification-count">{notifications.length} pending</span>
                  </div>
                  <div className="ireed-notification-list">
                    {notificationsLoading ? (
                      <div className="ireed-notification-empty">Loading notifications...</div>
                    ) : notifications.length ? notifications.map((item) => (
                      <div key={item.id} className="ireed-notification-item" onClick={() => { goToPage(item.page); setNotificationsOpen(false); }}>
                        <span className={`ireed-notification-icon ${item.kind.toLowerCase()}`}>
                          {item.kind === 'Blog' ? <ReadOutlined /> : item.kind === 'Article' ? <FileTextOutlined /> : <IdcardOutlined />}
                        </span>
                        <div className="ireed-notification-copy">
                          <div className="ireed-notification-item-title">{item.title}</div>
                          <div className="ireed-notification-description">{item.description}</div>
                          <div className="ireed-notification-time">{relativeTime(item.createdAt)}</div>
                        </div>
                      </div>
                    )) : (
                      <div className="ireed-notification-empty">You are all caught up.</div>
                    )}
                  </div>
                </div>
              )}
            >
              <Tooltip title={`Notifications (${notifications.length} pending)`}>
                <Badge count={notifications.length} size="small" offset={[-2, 2]} overflowCount={99}>
                  <button type="button" className="ireed-icon-btn" aria-label="Open notifications"><BellOutlined /></button>
                </Badge>
              </Tooltip>
            </Dropdown>

            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
              <button type="button" className="ireed-icon-btn" aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} onClick={toggleFullscreen}>
                {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              </button>
            </Tooltip>

            <Tooltip title="Hard refresh current page">
              <button type="button" className="ireed-icon-btn" aria-label="Hard refresh current page" onClick={hardRefresh}>
                <ReloadOutlined />
              </button>
            </Tooltip>

            <div style={{ width: 1, height: 22, background: '#e5e7eb' }} />

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
              <Tooltip title="Account options">
                <Space size={8} align="center" style={{ cursor: 'pointer' }}>
                  <Avatar
                    size={34}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#1677ff' }}
                  />
                  <div style={{ lineHeight: 1.25 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                      Hi, {userDetails?.name || 'Guest'}
                    </div>
                    <div style={{ fontSize: 11.5, color: '#9ca3af' }}>
                      {userDetails?.role ? toTitleCase(userDetails.role) : 'Administrator'}
                    </div>
                  </div>
                  <DownOutlined style={{ fontSize: 10, color: '#9ca3af' }} />
                </Space>
              </Tooltip>
            </Dropdown>
          </Space>
        </Header>

        {/* Page content */}
        <Content
          className="ireed-page-scroll"
          style={{ padding: 16, margin: 0, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
        >
          {selectedKey === 'dashboard' ? (
            <DashboardHome
              items={filteredItems}
              pendingTrainerCount={pendingTrainerCount}
              trainerStats={trainerStats}
              audienceStats={audienceStats}
              experienceBuckets={experienceBuckets}
              recentTrainers={recentTrainers}
              userName={userDetails?.name}
              userRole={userDetails?.role}
              onNavigate={goToPage}
            />
          ) : (
            PAGE_MAP[selectedKey] ?? (
              <div style={{
                height: '100%', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: '#aaa', fontSize: 15,
              }}>
                Select a menu item to get started
              </div>
            )
          )}
        </Content>
      </Layout>

      {/* Change Password Drawer */}
      <ChangePasswordDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        userDetails={userDetails}
      />
    </Layout>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// ChangePasswordDrawer
// ═════════════════════════════════════════════════════════════════════════════
const ChangePasswordDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  userDetails: UserDetails | null;
}> = ({ open, onClose, userDetails }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const handleSubmit = async (values: { oldPassword: string; newPassword: string }) => {
    setLoading(true);
    try {
      const res = await apiFetch(API_ENDPOINTS.passwordChange, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emailId: userDetails?.emailId,
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        }),
      });
      const data = await res.json();

      if (res.ok) {
        message.success('Password changed! Please log in again.');
        handleLogout();
      } else {
        message.error(data?.message || 'Failed to change password');
      }
    } catch {
      message.error('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title="Change Password"
      placement="right"
      width={360}
      onClose={onClose}
      open={open}
      destroyOnClose
    >
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        <Form.Item
          name="oldPassword"
          label="Current Password"
          rules={[{ required: true, message: 'Enter your current password' }]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder="Current password" />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="New Password"
          rules={[
            { required: true, message: 'Enter a new password' },
            { min: 6, message: 'Minimum 6 characters' },
          ]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder="New password" />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirm New Password"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Please confirm your password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value)
                  return Promise.resolve();
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder="Confirm new password" />
        </Form.Item>

        <Button type="primary" htmlType="submit" block loading={loading} style={{ marginTop: 4 }}>
          Update Password
        </Button>
      </Form>
    </Drawer>
  );
};

export default AdminLayout;